import React, {
  useState, useEffect, useMemo, useRef, useCallback
} from 'react';
import PropTypes from 'prop-types';
import { getAdvisorAutocompleteAPI } from '@common/api/api';
import queryString from 'query-string';
import classes from './classes.module.scss';
import AppLink from '../routes/app_link';
import { fuzzyMatch, highlightMatch } from '../../../common/config/utils';

const Autocomplete = ({
  t, query, clickSource, className, shouldClose
}) => {
  const trimmedQuery = useMemo(() => query?.trim() || '', [query]);
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const isRequestInProgress = useRef(false);
  const containerRef = useRef(null);

  // Outside click handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Reopen when query changes
  useEffect(() => {
    if (trimmedQuery && !shouldClose) {
      setIsOpen(true);
    }
  }, [trimmedQuery, shouldClose]);

  useEffect(() => {
    const urlQuery = queryString.parse(window.location.search).query;
    if (trimmedQuery && trimmedQuery === urlQuery) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (shouldClose) {
      setIsOpen(false);
    }
  }, [shouldClose]);

  const handleItemClick = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!trimmedQuery || advisors.length > 0 || isRequestInProgress.current) {
      return;
    }

    isRequestInProgress.current = true;
    setLoading(true);
    getAdvisorAutocompleteAPI()
      .then((response) => {
        const advisorsArray = Array.isArray(response) ? response : (response.advisors || []);
        setAdvisors(advisorsArray);
        setLoading(false);
        isRequestInProgress.current = false;
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error loading advisors:', error);
        setLoading(false);
        isRequestInProgress.current = false;
      });
  }, [trimmedQuery, advisors.length]);

  const matches = useMemo(() => {
    if (!trimmedQuery) return [];

    return advisors
      .map((advisor) => {
        const match = fuzzyMatch(trimmedQuery, advisor.nickname);
        if (!match) return null;

        return {
          ...advisor,
          matchIndex: match.positions[0],
          matchDistance: match.distance
        };
      })
      .filter((advisor) => advisor !== null)
      .sort((a, b) => {
        // Exact matches (distance 0) should rank higher than typo matches (distance 1)
        if (a.matchDistance !== b.matchDistance) {
          return a.matchDistance - b.matchDistance;
        }
        // Then sort by position (earlier matches first)
        return a.matchIndex - b.matchIndex;
      })
      .slice(0, 5);
  }, [trimmedQuery, advisors]);

  if (!trimmedQuery || !isOpen) return null;

  const autocompleteClassName = className
    ? `${ classes.autocomplete } ${ className }`
    : classes.autocomplete;

  if (loading) {
    return (
      <div ref={ containerRef } className={ autocompleteClassName }>
        <div className={ classes.autocompleteLoading }>
          { t('loading_advisors') }
        </div>
      </div>
    );
  }

  if (matches.length === 0) {
    return null;
  }

  return (
    <div ref={ containerRef } className={ autocompleteClassName }>
      { matches.map((advisor) => (
        <AppLink
          key={ advisor.slug }
          to={ { pathname: advisor.slug, state: { clickSource, startChatClickSource: clickSource } } }
          className={ classes.autocompleteItem }
          onClick={ handleItemClick }
        >
          { advisor.profilePictureUrl && (
            <img
              src={ advisor.profilePictureUrl }
              alt={ advisor.nickname }
              className={ classes.autocompleteAvatar }
            />
          ) }
          <span className={ classes.autocompleteNickname }>
            { highlightMatch(advisor.nickname, trimmedQuery) }
          </span>
        </AppLink>
      )) }
    </div>
  );
};

Autocomplete.propTypes = {
  t: PropTypes.func.isRequired,
  query: PropTypes.string.isRequired,
  clickSource: PropTypes.string.isRequired,
  className: PropTypes.string
};

Autocomplete.defaultProps = {
  className: ''
};

export default Autocomplete;
